import './Controls.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { FavoriteIcon } from '../../../../components/Icons/FavoriteIcon';
import { Likedicon } from '../../../../components/Icons/LikedIcon';

import {
  MuteIcon,
  PhoneHangupIcon,
  ShareScreenIcon,
  UnmuteIcon,
} from '../../../../components/Icons';
import { useFavorites } from '../../../../hooks/useFavorites';
import { checkFavouriteCompanions } from '../../../../services/api';
import { Tooltip } from '../../../../components/Tooltip';
import { TooltipHandle } from '../../../../components/Tooltip/Tooltip';
import { SessionMentor } from '../../../../types/mentor';
import { AudioControl } from './AudioControl';
import { UploadInput } from './UploadInput';

interface IProps {
  isMuted: boolean;
  controls?: boolean;
  companion: SessionMentor | undefined;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isScreenSharing?: boolean;
  showScreenShareButton?: boolean;
  isConnected: boolean;
  isTouchVisible?: boolean;
  disableEndButton?: boolean;
  disableUploadButton?: boolean;
  isInterview?: boolean;
  secondsBalance?: number | null;
  setMuted: (muted: boolean) => void;
  onScreenShare?: () => Promise<void>;
  handleConnect: () => void;
  handleDisconnect: (preventNavigation?: boolean) => void;
  handleReset?: () => void;
}

export const Controls: React.FC<IProps> = ({
  isMuted,
  companion,
  audioRef,
  isConnected,
  isScreenSharing = false,
  showScreenShareButton,
  disableEndButton = false,
  disableUploadButton = false,
  isInterview = false,
  isTouchVisible = false,
  secondsBalance,
  setMuted,
  onScreenShare,
  handleConnect,
  handleDisconnect,
  handleReset,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { toggleFavorite } = useFavorites();
  const tooltipRefs = useRef<Record<string, TooltipHandle>>({});
  const [favoriteCompanionIds, setFavoriteCompanionIds] = useState<Set<string>>(
    new Set(),
  );
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);

  // Persona + favorites APIs sometimes disagree on number vs string; normalize for Set lookups.
  const rawCompanionKey =
    companion?.mentorId ?? companion?.id ?? undefined;
  const companionId =
    rawCompanionKey !== undefined && rawCompanionKey !== null && rawCompanionKey !== ''
      ? String(rawCompanionKey)
      : undefined;
  const companionSlug = companion?.slug || '';
  const companionName = `${companion?.firstName || ''} ${
    companion?.lastName || ''
  }`.trim();
  const isCompanionFavorited = useMemo(
    () => !!companionId && favoriteCompanionIds.has(companionId),
    [companionId, favoriteCompanionIds],
  );

  const handleMute = () => {
    setMuted(!isMuted);
  };

  const handleShareScreen = async (e: React.MouseEvent) => {
    if (e.currentTarget) {
      (e.currentTarget as HTMLButtonElement)?.blur();
    }
    if (!onScreenShare) return;

    try {
      await onScreenShare();
    } catch (error) {
      console.error('Screen sharing failed:', error);
    }
  };

  const handleTouchEnd = (id: string) => {
    tooltipRefs.current[id]?.closeWithDelay();
  };

  useEffect(() => {
    const loadCompanionFavoriteState = async () => {
      if (!isAuthenticated) {
        setFavoriteCompanionIds(new Set());
        return;
      }
      if (!companionId) {
        setFavoriteCompanionIds(new Set());
        return;
      }

      try {
        const favoritedMap = await checkFavouriteCompanions([companionId]);
        const liked = favoritedMap[companionId] === true;
        setFavoriteCompanionIds(
          liked ? new Set([companionId]) : new Set(),
        );
      } catch (error) {
        console.error('Failed to check companion favorite status:', error);
      }
    };

    loadCompanionFavoriteState();
  }, [isAuthenticated, companionId]);

  const handleFavoriteToggle = useCallback(async () => {
    if (!isAuthenticated || !companionId || isFavoriteLoading) return;

    const currentlyLiked = favoriteCompanionIds.has(companionId);
    setIsFavoriteLoading(true);
    setFavoriteCompanionIds((prev) => {
      const next = new Set(prev);
      if (currentlyLiked) {
        next.delete(companionId);
      } else {
        next.add(companionId);
      }
      return next;
    });

    try {
      await toggleFavorite(
        'companion',
        companionId,
        currentlyLiked,
        companionName,
        companion?.firstName || companion?.lastName,
        undefined,
        companionSlug,
      );
    } catch {
      setFavoriteCompanionIds((prev) => {
        const next = new Set(prev);
        if (currentlyLiked) {
          next.add(companionId);
        } else {
          next.delete(companionId);
        }
        return next;
      });
    } finally {
      setIsFavoriteLoading(false);
    }
  }, [
    companion?.firstName,
    companion?.lastName,
    companionId,
    companionName,
    companionSlug,
    favoriteCompanionIds,
    isAuthenticated,
    isFavoriteLoading,
    toggleFavorite,
  ]);

  return (
    <div
      className={`p-4 z-[102] w-fit tc-mv-blur text-white text-center whitespace-nowrap transition duration-400 shadow-[0_0_5px_rgba(0,0,0,0.4)] group-hover:opacity-100 ${
        isTouchVisible || !isConnected ? 'opacity-100' : 'opacity-0'
      }`}>
      {!isInterview && (
        <>
          <div className="relative">
            <div className="min-w-0 px-10">
              <h3 className="text-2xl font-[DM_Serif_Text] break-words">{`${companion?.firstName} ${companion?.lastName}`}</h3>
              <p className="text-sm mb-3 break-words">{companion?.jobTitle}</p>
            </div>
            {isAuthenticated && companionId && (
              <button
                onClick={handleFavoriteToggle}
                disabled={isFavoriteLoading}
                className="absolute top-0 right-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-60">
                {isFavoriteLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isCompanionFavorited ? (
                  <Likedicon width={14} height={14} />
                ) : (
                  <FavoriteIcon width={14} height={14} />
                )}
              </button>
            )}
          </div>
        </>
      )}

      <div className="flex gap-2 justify-center h-10">
        {isConnected ? (
          <>
            <Tooltip
              ref={(el) => {
                if (el) {
                  tooltipRefs.current['mute'] = el;
                }
              }}
              content={isMuted ? t('avatar.unmute') : t('avatar.mute')}
              anchor="#mute"
            />
            <button
              className="tc-waveform-icon w-[40px] align-middle"
              onClick={handleMute}
              id="mute"
              onTouchEnd={() => handleTouchEnd('mute')}>
              {isMuted ? <MuteIcon /> : <UnmuteIcon />}
            </button>
            <AudioControl audioRef={audioRef} />
            {!disableUploadButton && (
              <UploadInput
                avatar_id={companion?.avatar_id || ''}
                mentorId={companion?.mentorId || ''}
              />
            )}
            {showScreenShareButton && (
              <>
                <Tooltip
                  content={isScreenSharing ? t('avatar.stopSharing') : t('avatar.shareScreen')}
                  anchor="#share-screen"
                  ref={(el) => {
                    if (el) {
                      tooltipRefs.current['share-screen'] = el;
                    }
                  }}
                />
                <button
                  className={`tc-waveform-icon w-[40px] align-middle ${
                    isScreenSharing ? 'text-black !bg-white' : 'text-white'
                  }`}
                  id="share-screen"
                  onClick={handleShareScreen}
                  onTouchEnd={() => handleTouchEnd('share-screen')}>
                  <ShareScreenIcon />
                </button>
              </>
            )}

            {!disableEndButton && (
              <>
                <Tooltip
                  content={t('avatar.endCall')}
                  anchor="#end-call"
                  ref={(el: TooltipHandle | null) => {
                    if (el) {
                      tooltipRefs.current['end-call'] = el;
                    }
                  }}
                />
                <button
                  onTouchEnd={() => handleTouchEnd('end-call')}
                  onClick={() => handleDisconnect(false)}
                  id="end-call"
                  className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-stop flex-shrink-0">
                  <PhoneHangupIcon className="!text-red-400" />
                </button>
              </>
            )}
          </>
        ) : secondsBalance === 0 ? (
          <Link
            to="/pricing"
            onClick={handleReset}
            className="px-6 py-2 bg-white hover:bg-white/90 transition-colors duration-200 text-black rounded-full text-sm font-medium font-[NapsterSans] whitespace-nowrap">
            {t('avatar.buyCreditsToStart')}
          </Link>
        ) : (
          <>
            <Tooltip
              ref={(el) => {
                if (el) {
                  tooltipRefs.current['startCall'] = el;
                }
              }}
              content={t('avatar.startCall')}
              anchor="#startCall"
            />
            <button
              className="tc-waveform-icon w-[40px] align-middle !text-red-500"
              onClick={handleConnect}
              id="startCall"
              onTouchEnd={() => handleTouchEnd('mute')}>
              <i className="fa-solid fa-microphone-slash" />
            </button>
          </>
        )}
      </div>
      {!isInterview && (
        <div className="text-[10px]  w-full text-white bottom-0 text-center mt-2 mx-auto max-w-[290px] px-4 opacity-50">
          {t('avatar.disclaimerLine1')}
          <br />
          {t('avatar.disclaimerLine2')}
        </div>
      )}
    </div>
  );
};
